import { z } from 'zod';
import { contentLanguageSchema } from '../translation/index';
import { languageSchema } from '../auth/index';
import { pageSchema } from '../common/pagination';

// --- Medewerkers ---------------------------------------------------------------------

export const personStatusSchema = z.enum(['active', 'deactivated']);
export type PersonStatus = z.infer<typeof personStatusSchema>;

/**
 * Wat voor medewerker iemand is (Stap 31, ADR-0077): vast, uitzendkracht of aannemer.
 *
 * Een eigenschap van de persoon en geen aparte soort account: een uitzendkracht meldt zich aan,
 * leest en vinkt af zoals iedereen. Wat anders is, is dat hij een einddatum heeft, een smallere
 * rol krijgt en dat een bedrijf kan kiezen of berichten aan "iedereen" ook hem bereiken.
 */
export const personTypeSchema = z.enum(['employee', 'temporary', 'contractor']);
export type PersonType = z.infer<typeof personTypeSchema>;

/** Hoe ver de einddatum van een tijdelijk account mag liggen (Stap 31). [AANNAME] */
export const TEMPORARY_MAX_MONTHS = 12;

/**
 * Het personeelsnummer is het nummer waarmee iemand op de vloer bekend is: het staat op
 * zijn badge en straks op zijn aanmeldscherm (Stap 5). Uniek binnen één bedrijf, niet
 * daarbuiten — twee klanten mogen allebei een "1042" hebben.
 */
export const employeeNumberSchema = z
  .string()
  .min(1)
  .max(32)
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
    'Gebruik letters, cijfers, punt, streepje of schuine streep.',
  );

export const personSchema = z.object({
  id: z.guid(),
  photoFileId: z.guid().nullable().default(null),
  employeeNumber: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().nullable(),
  preferredLanguage: languageSchema,
  /**
   * De taal waarin deze medewerker berichten leest, als hij er een koos.
   *
   * Niet dezelfde vraag als `preferredLanguage` (ADR-0043): dat is de taal van de interface en van
   * brieven, en die bestaat in drie talen. Dit is de taal van de inhoud, en dat is alles wat de
   * vertaaldienst kan. Leeg betekent: lees in de brontaal van het bericht.
   */
  readingLanguage: contentLanguageSchema.nullable().default(null),
  status: personStatusSchema,
  /**
   * De primaire vestiging van deze medewerker (Stap 29).
   *
   * `null` bij een bedrijf met één vestiging, en dat is de normale toestand. Dat iemand op meer
   * sites komt, staat in zijn groepen; dit is waar hij hoort - voor de tijdzone van zijn
   * planning en voor de vraag onder welke sitebeheerder hij valt.
   */
  siteId: z.guid().nullable().default(null),
  plannedExitDate: z.string().date().nullable(),
  deactivatedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /** Vast, uitzendkracht of aannemer (Stap 31). Bij een tijdelijke is `plannedExitDate` de einddatum. */
  personType: personTypeSchema.default('employee'),
  /** Het uitzendbureau of de aannemer waar hij vandaan komt; vrije tekst. */
  originCompany: z.string().nullable().default(null),
});
export type Person = z.infer<typeof personSchema>;

/** Eén medewerker met alles erbij: veldwaarden en groepen. */
export const personDetailSchema = personSchema.extend({
  fieldValues: z.record(z.string(), z.string().nullable()),
  groupIds: z.array(z.guid()),
});
export type PersonDetail = z.infer<typeof personDetailSchema>;

export const createPersonRequestSchema = z.object({
  firstName: z.string().min(1).max(80),
  lastName: z.string().min(1).max(80),
  employeeNumber: employeeNumberSchema.nullable().optional(),
  email: z.string().email().max(320).nullable().optional(),
  preferredLanguage: languageSchema.default('nl'),
  plannedExitDate: z.string().date().nullable().optional(),
  siteId: z.guid().nullable().optional(),
  fieldValues: z.record(z.string(), z.string().nullable()).optional(),
  groupIds: z.array(z.guid()).optional(),
  /** Een tijdelijke (`temporary`, `contractor`) heeft altijd een `plannedExitDate` (Stap 31). */
  personType: personTypeSchema.optional(),
  originCompany: z.string().trim().max(120).nullable().optional(),
});
export type CreatePersonRequest = z.infer<typeof createPersonRequestSchema>;

/**
 * Snel een tijdelijk account (Stap 31): wat een teamleider op zijn telefoon invult.
 *
 * Geen personeelsnummer - dat maakt `directory` - en geen e-mailadres: een uitzendkracht meldt zich
 * aan met nummer en PIN. Een teamleider kiest alleen groepen die hij beheert, en wordt zelf de
 * leidinggevende; HR kiest vrij.
 */
export const createTemporaryPersonRequestSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  personType: z.enum(['temporary', 'contractor']).default('temporary'),
  originCompany: z.string().trim().max(120).nullable().default(null),
  /** De dag waarop het account uitgaat. Verplicht, en hoogstens twaalf maanden vooruit. */
  endDate: z.string().date(),
  groupIds: z.array(z.guid()).max(10).default([]),
  /** Alleen voor HR; een teamleider wordt zelf de leidinggevende. */
  managerId: z.guid().nullable().default(null),
  siteId: z.guid().nullable().default(null),
  preferredLanguage: languageSchema.default('nl'),
});
export type CreateTemporaryPersonRequest = z.infer<typeof createTemporaryPersonRequestSchema>;

/** Het antwoord: de persoon, de activatiecode om meteen te tonen en de QR-uitnodiging (REG-1). */
export const temporaryPersonCreatedSchema = z.object({
  person: personSchema,
  employeeNumber: z.string(),
  activationCode: z.string(),
  activationExpiresAt: z.string().datetime(),
  /** Persoonlijke uitnodiging: de code `XXXX-XXXX` en de link in de QR-code. */
  invitationCode: z.string().nullable(),
  invitationLink: z.string().nullable(),
});
export type TemporaryPersonCreated = z.infer<typeof temporaryPersonCreatedSchema>;

export const updatePersonRequestSchema = createPersonRequestSchema
  .partial()
  .extend({ status: personStatusSchema.optional() });
export type UpdatePersonRequest = z.infer<typeof updatePersonRequestSchema>;

/**
 * Uit dienst: meteen of op een datum.
 *
 * Een uitdienst is een harde grens — wie vandaag vertrekt, ziet vandaag niets meer. Het
 * oude platform zette zo iemand terug naar "volger", en dat is geen grens maar een
 * gradatie. De reden is vrije tekst en alleen voor HR zichtbaar.
 */
export const deactivateRequestSchema = z.object({
  effective: z.union([z.literal('now'), z.string().date()]).default('now'),
  reason: z.string().max(500).optional(),
});
export type DeactivateRequest = z.infer<typeof deactivateRequestSchema>;

/**
 * Meerdere mensen in één keer uit dienst (FBK-4): aangevinkt in de lijst, of uit een CSV met een
 * uitdienstlijst. Dezelfde uitdienst als voor één persoon, per persoon; niets wordt gewist.
 */
export const bulkDeactivateRequestSchema = deactivateRequestSchema.extend({
  personIds: z.array(z.guid()).min(1).max(500),
});
export type BulkDeactivateRequest = z.infer<typeof bulkDeactivateRequestSchema>;

export const bulkDeactivateResultSchema = z.object({
  /** Uit dienst gezet of ingepland. */
  done: z.number().int(),
  /** Niet gevonden of al uit dienst; met de reden van de server. */
  skipped: z.array(z.object({ personId: z.guid(), reason: z.string() })),
});
export type BulkDeactivateResult = z.infer<typeof bulkDeactivateResultSchema>;

/** Personeelsnummers of e-mailadressen uit een uitdienstlijst, om eerst te tonen wie het is. */
export const matchPeopleRequestSchema = z.object({
  identifiers: z.array(z.string().trim().min(1).max(320)).min(1).max(2000),
});
export type MatchPeopleRequest = z.infer<typeof matchPeopleRequestSchema>;

export const matchPeopleResultSchema = z.object({
  matched: z.array(
    z.object({
      identifier: z.string(),
      personId: z.guid(),
      name: z.string(),
      employeeNumber: z.string().nullable(),
      status: personStatusSchema,
    }),
  ),
  /** Wat in de lijst stond en bij niemand past. */
  unmatched: z.array(z.string()),
});
export type MatchPeopleResult = z.infer<typeof matchPeopleResultSchema>;

export const deactivationSchema = z.object({
  personId: z.guid(),
  status: personStatusSchema,
  plannedExitDate: z.string().date().nullable(),
  deactivatedAt: z.string().datetime().nullable(),
  reason: z.string().nullable(),
});
export type Deactivation = z.infer<typeof deactivationSchema>;

/**
 * Zoeken en filteren. `search` kijkt naar naam en personeelsnummer; `field` filtert op
 * de waarde van één gegevensveld, bijvoorbeeld `field=afdeling&fieldValue=onderhoud`.
 */
export const personQuerySchema = z.object({
  search: z.string().max(100).optional(),
  status: personStatusSchema.optional(),
  groupId: z.guid().optional(),
  field: z.string().max(64).optional(),
  fieldValue: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  /** Alleen mensen van deze vestiging (Stap 29). `none` zijn de mensen zonder vestiging. */
  siteId: z.string().optional(),
  /**
   * Soort medewerker (Stap 31). `external` is uitzendkrachten en aannemers samen: de filter
   * "Tijdelijk" in de lijst.
   */
  type: z.enum(['employee', 'temporary', 'contractor', 'external']).optional(),
  /** Alleen wie vóór deze datum uitgaat: de aflopende accounts. */
  endingBefore: z.string().date().optional(),
  /**
   * De sorteerkolom (FBK-4). Standaard op naam. `endDate`: eerst wie het eerst uitgaat, zonder
   * einddatum achteraan. Bij gelijke waarde volgt de naam.
   */
  sort: z.enum(['name', 'employeeNumber', 'language', 'status', 'endDate']).optional(),
  direction: z.enum(['asc', 'desc']).optional(),
});
export type PersonQuery = z.infer<typeof personQuerySchema>;

/**
 * Het filter op activatie (FBK-4) gaat over accounts, en die zijn van `identity`. Die module mag
 * `directory` aanroepen, niet omgekeerd; daarom heeft `identity` een eigen route met dezelfde
 * filters plus `activation`, en geeft ze aan `directory` de mensen mee die wel of niet passen.
 */
export const activationFilterSchema = z.enum(['active', 'pending_activation', 'disabled', 'none']);
export type ActivationFilter = z.infer<typeof activationFilterSchema>;

export const personByActivationQuerySchema = personQuerySchema.extend({
  activation: activationFilterSchema,
});
export type PersonByActivationQuery = z.infer<typeof personByActivationQuerySchema>;

export const personPageSchema = pageSchema(personSchema);
export type PersonPage = z.infer<typeof personPageSchema>;

// --- Gegevensvelden ------------------------------------------------------------------

export const fieldTypeSchema = z.enum(['text', 'number', 'date', 'select']);
export type FieldType = z.infer<typeof fieldTypeSchema>;

/**
 * De sleutel is wat in filters en imports gebruikt wordt en ligt vast na het aanmaken;
 * het label is per taal en mag altijd wijzigen.
 */
export const fieldKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'Gebruik kleine letters, cijfers en liggende streepjes.');

export const fieldLabelSchema = z.partialRecord(languageSchema, z.string().min(1).max(80));

export const fieldDefinitionSchema = z.object({
  id: z.guid(),
  key: z.string(),
  label: fieldLabelSchema,
  type: fieldTypeSchema,
  options: z.array(z.string()).nullable(),
  isSystem: z.boolean(),
  archivedAt: z.string().datetime().nullable(),
  valueCount: z.number().int(),
  /**
   * Mag dit veld op het collegascherm staan? (SCH-3, ADR-0069)
   *
   * **Standaard uit, altijd.** Een bedrijf dat een veld "loonschaal" of "geboortedatum" via de
   * import vult, hoort dat niet ineens aan de hele vloer te tonen omdat er een scherm bijkwam.
   * Opt-in, nooit opt-out.
   */
  visibleToColleagues: z.boolean(),
});
export type FieldDefinition = z.infer<typeof fieldDefinitionSchema>;

export const createFieldRequestSchema = z
  .object({
    key: fieldKeySchema,
    label: fieldLabelSchema,
    type: fieldTypeSchema,
    options: z.array(z.string().min(1).max(80)).max(200).optional(),
  })
  .refine((value) => value.type !== 'select' || (value.options?.length ?? 0) > 0, {
    message: 'Een keuzeveld heeft minstens één optie nodig.',
    path: ['options'],
  });
export type CreateFieldRequest = z.infer<typeof createFieldRequestSchema>;

/** Het type en de sleutel liggen vast: bestaande waarden zouden anders van betekenis veranderen. */
export const updateFieldRequestSchema = z.object({
  label: fieldLabelSchema.optional(),
  options: z.array(z.string().min(1).max(80)).max(200).optional(),
  visibleToColleagues: z.boolean().optional(),
});
export type UpdateFieldRequest = z.infer<typeof updateFieldRequestSchema>;

/**
 * De tellingen voor het startscherm van het beheerportaal (Extra 2, punt 8).
 *
 * Tellingen en geen lijst: het startscherm toont getallen, en de lijst van vijfhonderd mensen
 * ophalen om er drie van te maken, is wat een beginscherm traag maakt.
 *
 * `withEmployeeNumber` is de noemer van de activatiegraad en staat daarom hier en niet bij
 * `identity`: wie geen personeelsnummer heeft, kan niet activeren, en die hoort dus niet in de
 * breuk. Wat `identity` erover weet - hoeveel accounts er werkelijk actief zijn - vraagt het
 * startscherm apart; de twee modules kennen elkaar niet.
 */
export const peopleStatsSchema = z.object({
  active: z.number().int(),
  deactivated: z.number().int(),
  withEmployeeNumber: z.number().int(),
  /**
   * Vestiging-id → hoeveel actieve mensen daar hun primaire vestiging hebben (Stap 29).
   *
   * Hier en niet op `Site` zelf: `tenants` bezit de vestigingen, `directory` de mensen, en een
   * module telt wat ze bezit (ADR-0016). Zou `Site` een `peopleCount` dragen, dan moest `tenants`
   * in het schema `directory` kijken - precies wat de architectuurcheck tegenhoudt.
   *
   * Mensen zonder vestiging staan onder de sleutel `''`.
   */
  perSite: z.record(z.string(), z.number().int()),
});
export type PeopleStats = z.infer<typeof peopleStatsSchema>;

export const setFieldValuesRequestSchema = z.object({
  /** Sleutel → waarde; `null` wist de waarde. */
  values: z.record(z.string(), z.string().max(500).nullable()),
});
export type SetFieldValuesRequest = z.infer<typeof setFieldValuesRequestSchema>;

// --- Groepen -------------------------------------------------------------------------

export const groupKindSchema = z.enum(['department', 'shift', 'line', 'site', 'other']);
export type GroupKind = z.infer<typeof groupKindSchema>;

/**
 * Een groepssoort van het bedrijf (FBK-5, ADR-0092): een eigen naam bovenop een van de vijf vaste
 * soorten (`baseKind`), waar de code op leunt. `name` null is de vertaalde standaardnaam van de
 * basis; dat zijn de vijf vaste soorten (`isSystem`) tot iemand ze hernoemt.
 */
export const groupKindDefinitionSchema = z.object({
  id: z.guid(),
  baseKind: groupKindSchema,
  name: z.string().nullable(),
  color: z.string().nullable(),
  position: z.number().int(),
  isSystem: z.boolean(),
  archived: z.boolean(),
  /** Hoeveel groepen deze soort dragen; een soort in gebruik kan uit, niet weg. */
  groupCount: z.number().int(),
});
export type GroupKindDefinition = z.infer<typeof groupKindDefinitionSchema>;

const kindColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Een kleur als #RRGGBB.')
  .nullable();

export const createGroupKindRequestSchema = z.object({
  name: z.string().trim().min(1).max(40),
  baseKind: groupKindSchema.default('other'),
  color: kindColorSchema.optional(),
});
export type CreateGroupKindRequest = z.infer<typeof createGroupKindRequestSchema>;

/** Hernoemen, kleur, volgorde, uit- en aanzetten. `name: null` zet een vaste soort terug. */
export const updateGroupKindRequestSchema = z.object({
  name: z.string().trim().min(1).max(40).nullable().optional(),
  color: kindColorSchema.optional(),
  position: z.number().int().min(0).max(1000).optional(),
  archived: z.boolean().optional(),
});
export type UpdateGroupKindRequest = z.infer<typeof updateGroupKindRequestSchema>;

export const groupSchema = z.object({
  id: z.guid(),
  name: z.string(),
  kind: groupKindSchema,
  /** De soort van het bedrijf (FBK-5), of null voor de vaste soort van `kind`. */
  kindId: z.guid().nullable().default(null),
  /** De eigen naam van die soort; null is de standaardnaam van `kind`. */
  kindName: z.string().nullable().default(null),
  /** De groep waar deze onder valt (FBK-5); null bovenaan. */
  parentGroupId: z.guid().nullable().default(null),
  /**
   * De vestiging waar deze groep bij hoort (Stap 29), of `null` voor een groep over alle
   * vestigingen heen - en dat blijft de standaard.
   *
   * Bij een groep van soort `site` wijst dit naar de vestiging die bij die groep hoort: de
   * migratie van Stap 29 heeft de bestaande site-groepen omgezet en gekoppeld, zodat een
   * doelgroep die op zo'n groep gebouwd is, blijft doen wat ze deed.
   */
  siteId: z.guid().nullable().default(null),
  externalKey: z.string().nullable(),
  memberCount: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Group = z.infer<typeof groupSchema>;

export const createGroupRequestSchema = z.object({
  /** Leeg laten voor een groep die over alle vestigingen heen gaat. */
  siteId: z.guid().nullable().optional(),
  name: z.string().min(1).max(80),
  kind: groupKindSchema.default('other'),
  /** Een soort van het bedrijf (FBK-5); dan volgt `kind` de basis van die soort. */
  kindId: z.guid().nullable().optional(),
  /** Sleutel uit het HR-systeem; maakt een herimport herkenbaar (Stap 6). */
  externalKey: z.string().min(1).max(64).nullable().optional(),
});
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = createGroupRequestSchema.partial();
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

export const addMembersRequestSchema = z.object({
  personIds: z.array(z.guid()).min(1).max(1000),
});
export type AddMembersRequest = z.infer<typeof addMembersRequestSchema>;

export const membershipChangeSchema = z.object({
  added: z.array(z.guid()),
  removed: z.array(z.guid()),
});
export type MembershipChange = z.infer<typeof membershipChangeSchema>;

// --- Eigen profiel -------------------------------------------------------------------

export const myProfileSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  employeeNumber: z.string().nullable(),
  email: z.string().email().nullable(),
  preferredLanguage: languageSchema,
  /** De taal waarin hij berichten leest; leeg is de brontaal (ADR-0043). */
  readingLanguage: contentLanguageSchema.nullable().default(null),
  /** Alleen het id: de URL om hem te tonen komt van `media` en is een minuut geldig. */
  photoFileId: z.guid().nullable(),
});
export type MyProfile = z.infer<typeof myProfileSchema>;

/**
 * Wat een medewerker zelf mag wijzigen: hoe zijn naam getoond wordt en in welke taal hij
 * het platform leest. Niet zijn personeelsnummer of afdeling — dat komt uit het
 * HR-systeem en zou anders stilletjes uit elkaar lopen.
 */
export const updateMyProfileRequestSchema = z.object({
  firstName: z.string().min(1).max(80).optional(),
  lastName: z.string().min(1).max(80).optional(),
  preferredLanguage: languageSchema.optional(),
  /** `null` zet de voorkeur terug: dan leest hij weer in de brontaal. */
  readingLanguage: contentLanguageSchema.nullable().optional(),
});
export type UpdateMyProfileRequest = z.infer<typeof updateMyProfileRequestSchema>;

/**
 * Eén collega, zoals een medewerker hem in de lijst ziet (Stap 34b, ADR-0051).
 *
 * Bewust smal. Dit is het gevoeligste scherm dat we aan medewerkers geven: wie de volledige lijst
 * kan zien, ziet de organisatiestructuur, en bij vertrek kan die lijst mee naar buiten. Daarom geen
 * e-mailadres, geen telefoonnummer, geen personeelsnummer, geen indiensttredingsdatum.
 *
 * **Afwijking van ADR-0051:** dat ADR noemt "functie en afdeling". Een functietitel bestaat niet in
 * `directory.person` en die erbij verzinnen is een datamodelbeslissing die bij HR hoort, niet bij de
 * bouwer. In plaats daarvan staan hier **afdeling en ploeg**, die wél bestaan — als groepen van
 * soort `department` en `shift`. Op een werkvloer is de ploeg trouwens het nuttigere gegeven: je
 * zoekt een collega om een dienst te ruilen.
 */
export const colleagueSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  photoFileId: z.guid().nullable(),
  /** Naam van de groep van soort `department`, als de medewerker er in één zit. */
  department: z.string().nullable(),
  /** Naam van de groep van soort `shift`. */
  shift: z.string().nullable(),
});
export type Colleague = z.infer<typeof colleagueSchema>;

/**
 * Eén collega, met alles wat dit bedrijf zichtbaar zette (SCH-3).
 *
 * `fields` is leeg bij een bedrijf dat niets instelde, en dat is de normale toestand — niet een
 * uitzondering. `email` en `site` staan apart omdat ze kolommen zijn en geen veld; ze hebben
 * dezelfde schakelaar en dezelfde standaard.
 */
export const colleagueDetailSchema = colleagueSchema.extend({
  email: z.string().nullable(),
  site: z.string().nullable(),
  /** Alleen de velden die het bedrijf zichtbaar zette, in de volgorde van de catalogus. */
  fields: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      value: z.string(),
    }),
  ),
});
export type ColleagueDetail = z.infer<typeof colleagueDetailSchema>;

/**
 * De filters boven de collega-lijst (SCH-4).
 *
 * De echte afdelingen van dít bedrijf, geen vaste lijst: de mockup noemt er zes die bij de ene
 * klant bestaan en bij de andere niet. `myShift` is de ploeg van de aanvrager — heeft hij er geen,
 * dan staat de knop "Mijn ploeg" er niet.
 */
export const colleagueFiltersSchema = z.object({
  departments: z.array(z.string()),
  myShift: z.string().nullable(),
});
export type ColleagueFilters = z.infer<typeof colleagueFiltersSchema>;

/** Wat een bedrijf zichtbaar zette buiten de veldencatalogus om. */
export const colleagueVisibilitySchema = z.object({
  showEmail: z.boolean(),
  showSite: z.boolean(),
});
export type ColleagueVisibility = z.infer<typeof colleagueVisibilitySchema>;

export const updateColleagueVisibilityRequestSchema = colleagueVisibilitySchema.partial();
export type UpdateColleagueVisibilityRequest = z.infer<
  typeof updateColleagueVisibilityRequestSchema
>;

export const colleaguePageSchema = z.object({
  items: z.array(colleagueSchema),
  nextCursor: z.string().nullable(),
  /**
   * Hoeveel collega's er in totaal aan de huidige zoekopdracht en filters voldoen (SCH-4).
   *
   * De voet van de lijst zegt "8 van 248 getoond", en dat getal hoort geteld te zijn in plaats van
   * geschat. Het telt mee met het filter: wie op "Productie" staat, leest hoeveel mensen er in
   * Productie zijn en niet hoeveel er in het bedrijf werken.
   */
  total: z.number().int(),
});
export type ColleaguePage = z.infer<typeof colleaguePageSchema>;

// --- Mijn team (Stap 17, TEAM-1) -----------------------------------------------------

/**
 * Eén lid van het team van een leidinggevende.
 *
 * Smaller dan `colleagueDetail` en breder dan `colleague`: een leidinggevende ziet van zijn
 * eigen mensen de accountstatus en het telefoonnummer, want dat is wat hij nodig heeft om
 * iemand aan de praat te krijgen. Van iemand buiten zijn team ziet hij niets van dit alles.
 */
export const teamMemberSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  photoFileId: z.guid().nullable(),
  /** Naam van de groep van soort `department`. */
  department: z.string().nullable(),
  /** Naam van de groep van soort `shift`. */
  shift: z.string().nullable(),
  /** Naam van de groep van soort `site`. */
  site: z.string().nullable(),
  /**
   * Of deze medewerker al een werkend account heeft.
   *
   * De drie woorden van `identity`, niet een vlag: het scherm toont "Nog niet geactiveerd" met
   * de uitleg "Vraag HR om een activatiecode", en dat is iets anders dan "gedeactiveerd".
   */
  accountStatus: z.enum(['active', 'pending_activation', 'disabled', 'none']),
  /** Uit dienst: het scherm zet deze rij onderaan op 60 % dekking en telt hem niet mee. */
  deactivated: z.boolean(),
  /**
   * Of meldingen op een toestel aanstaan, met het aantal toestellen.
   *
   * `null` betekent dat de kijker `notifications.push.view` niet heeft; het scherm laat de kaart
   * dan weg. Een `false` zou lezen als "uit", en dat is een ander antwoord dan "ik mag het niet
   * weten" (ADR-0048).
   */
  push: z.object({ enabled: z.boolean(), deviceCount: z.number().int() }).nullable(),
  /** Hoeveel verplichte berichten hij nog niet bevestigde; voor de badge "2 open". */
  openMandatory: z.number().int(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

/**
 * Eén team van de leidinggevende: zijn rapporteringslijn of een groep die hij beheert.
 *
 * `kind` staat erbij omdat het scherm de chips anders niet kan uitleggen: "Mijn rapportering"
 * is iets anders dan "Ploeg B", ook als er dezelfde mensen in zitten.
 */
export const teamSchema = z.object({
  /** `reports` heeft geen groep-id; dan staat hier het woord `reports`. */
  teamId: z.string(),
  label: z.string(),
  kind: z.enum(['reports', 'group']),
  members: z.array(teamMemberSchema),
  /** Leden die uit dienst zijn, onderaan en niet meegeteld (spec §4). */
  deactivatedMembers: z.array(teamMemberSchema),
});
export type Team = z.infer<typeof teamSchema>;

export const myTeamSchema = z.object({
  teams: z.array(teamSchema),
  /** Of de onderliggende groepen meegeteld zijn; de keuze van de kijker. */
  includesSubgroups: z.boolean(),
});
export type MyTeam = z.infer<typeof myTeamSchema>;

export const myTeamQuerySchema = z.object({
  /**
   * "Ook onderliggende groepen" (spec §2, frame 1). Standaard uit. Geen `z.coerce.boolean()`:
   * die maakt van de querystring `"false"` ook `true`.
   */
  subgroups: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((waarde) => waarde === true || waarde === 'true')
    .default(false),
});
export type MyTeamQuery = z.infer<typeof myTeamQuerySchema>;

/** De leidinggevende van een medewerker instellen (tab "Leidinggevende" in het persoonsdetail). */
export const setManagerRequestSchema = z.object({
  /** `null` haalt de lijn weg: deze persoon rapporteert aan niemand. */
  managerPersonId: z.guid().nullable(),
});
export type SetManagerRequest = z.infer<typeof setManagerRequestSchema>;

/**
 * Wat er in die tab staat: zijn leidinggevende, en wie aan hem rapporteert.
 *
 * Beide kanten in één antwoord, want HR stelt in de praktijk beide vragen tegelijk: wie is de
 * chef van deze persoon, en hoeveel mensen hangen er onder hem.
 */
export const managerInfoSchema = z.object({
  manager: z
    .object({
      personId: z.guid(),
      displayName: z.string(),
    })
    .nullable(),
  directReports: z.array(z.object({ personId: z.guid(), displayName: z.string() })),
  /** De groepen die deze persoon beheert; HR ziet in één oogopslag wat hij ziet. */
  managedGroups: z.array(z.object({ groupId: z.guid(), name: z.string() })),
});
export type ManagerInfo = z.infer<typeof managerInfoSchema>;

/** De beheerders van een groep. Niet hetzelfde als de leden (zie `directory.group_admin`). */
export const groupAdminsSchema = z.object({
  admins: z.array(z.object({ personId: z.guid(), displayName: z.string() })),
});
export type GroupAdmins = z.infer<typeof groupAdminsSchema>;

export const setGroupAdminsRequestSchema = z.object({
  personIds: z.array(z.guid()).max(50),
});
export type SetGroupAdminsRequest = z.infer<typeof setGroupAdminsRequestSchema>;

/** Een groep onder een andere hangen: "Lijn 3" in "Productie". */
export const setParentGroupRequestSchema = z.object({
  parentGroupId: z.guid().nullable(),
});
export type SetParentGroupRequest = z.infer<typeof setParentGroupRequestSchema>;
